import React, { useState, useContext } from 'react';
import styled from 'styled-components/macro';

import { fontkoKR } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';
import GTM from '@mirinae/apis/GoogleAnalytics';

import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { FormattedKoEnPhrase, FormattedKoPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import { getFields } from '@mirinae/lessons/modules/libs/elementLib';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';

const Stage = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
`;

const TargetArea = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const TargetWord = styled.div`
    ${os.isOthers ? `
        padding-right: 60px;
    ` : `
        padding-right: 25px;
    `}
`;

const ButtonsColumn = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${props => (props.show ? `
        width: max-content;
    ` : `
        max-width: 0px;
    `)}
`;

const ChoiceButton = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 15px;
    padding: 0 10px;
    width: 100%;
    height: 72px;
    background-color: ${lessonsPalette.primary.white};
    border: 1px solid ${lessonsPalette.grey[5]};
    box-sizing: border-box;
    box-shadow: 3px 3px 7px rgba(159, 188, 186, 0.4);
    border-radius: 10px;
    font-weight: 600;
    color: ${lessonsPalette.grey[0]};
    cursor: pointer;
    span {
        color: inherit !important;
    }
    :hover {
        background-color: ${lessonsPalette.lessons[4]};
        border-color: ${lessonsPalette.darken[0]};
        box-shadow: none;
    }
    &:first-child {
        margin-top: 0;
    }
`;

const BadChoiceButton = styled(ChoiceButton)`
    border-color: ${lessonsPalette.lessons[2]};
    background-color: ${lessonsPalette.lessons[3]};
    animation: wiggle 0.25s;
    box-shadow: none;

    &:hover {
        border-color: ${lessonsPalette.lessons[2]};
        background-color: ${lessonsPalette.lessons[3]};
    }

    @keyframes wiggle {
        0% { transform: rotate(0deg); }
        5% { transform: rotate(-5deg); }
       80% { transform: rotate(5deg); }
      100% { transform: rotate(0deg); }
    }
`;

const CorrectChoiceButton = styled(ChoiceButton)`
    background: ${lessonsPalette.lessons[4]};
    border-color: ${lessonsPalette.primary.main};
    box-shadow: none;
`;

const HiddenButton = styled(ChoiceButton)`
    visibility: hidden;
`;

const ChoiceButtonLabel = styled(FormattedKoPhrase)`
    font-family: ${fontkoKR};
    font-style: normal;
    font-weight: normal;
    font-size: 22px;
    line-height: 33px;
`;

const targetStyle = {
    koFontWeight: '600', koreanFontSize: '30px',
    enFontWeight: 'normal', englishFontSize: '18px',
};


const PickOne = ({ element }) => {
    const [badPick, setBadPick] = useState(null);
    const [complete, setComplete] = useState(false);
    const player = useContext(PlayerContext);

    const { correctAnswer: correctAnswerButton } = getFields(element, 'correctAnswer');
    const correctAnswerIndex = parseInt(correctAnswerButton.split(' ')[1], 10);

    const buttonClicked = (i) => {
        setBadPick(null);

        if (i === correctAnswerIndex) {
            setComplete(true);
            player.postEvent({ action: 'complete' }, element);
            GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
        } else {
            setBadPick(i);
            player.postEvent({ action: 'error' }, element);
            GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
        }
    };

    const buttonIsHidden = (i) => element.fieldsByName[`button${i}`].hiddenParts && element.fieldsByName[`button${i}`].hiddenParts.length > 0;

    return (
        <Element element={element}>
            <Stage>
                <TargetArea>
                    { complete ? <SuccessOverlay top={-55} left={1} /> : null }
                    <TargetWord>
                        <FormattedKoEnPhrase
                            phrase={complete ? element.fieldsByName.finalForm : element.fieldsByName.target}
                            phraseStyle={{ ...element.style, ...targetStyle, adaptiveFontScale: 0.85 }} />
                    </TargetWord>
                </TargetArea>
                <ButtonsColumn show={os.isOthers || !complete}>
                    { [1, 2, 3].filter(i => !buttonIsHidden(i)).map(i => {
                        let Button = badPick === i ? BadChoiceButton : ChoiceButton;
                        if (complete) {
                            if (i === correctAnswerIndex && os.isOthers) {
                                Button = CorrectChoiceButton;
                            } else {
                                Button = HiddenButton;
                            }
                        }

                        return element.fieldsByName[`button${i}`] && (
                            <Button data-t={`pickone-btn-${i}`} key={i} onClick={() => buttonClicked(i)}>
                                <ChoiceButtonLabel phrase={element.fieldsByName[`button${i}`]} phraseStyle={{ koFontWeight: '600', koreanFontSize: '22px', noTextTools: true }} />
                            </Button>
                        );
                    })}
                </ButtonsColumn>

            </Stage>
        </Element>
    );
};

export default PickOne;
